import api from "../api";
import { apiWriteThunk } from "../apiWriteThunk";
import {
  POST_DESIGN,
  GET_DESIGNS,
  DELETE_DESIGN,
  UPDATE_DESIGN,
} from "./designTypes";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { asCaught, errorPayload } from "../caughtError";
import { isDeleteRefusal } from "../deleteRefusal";
import type { AxiosResponse } from "axios";
import type {
  MessageResponse,
  DesignRecoverResponse,
  DesignResponse,
} from "../../../../shared/apiTypes";

// SCRUM-2119. Callers (DesignForm) await the body and catch a rejection, and
// createAsyncThunk's dispatch resolves to the action, so the wrapper unwraps.
const postDesign = apiWriteThunk(
  "designs/add",
  (data) => api.post<MessageResponse>("/api/design/add", data),
  (dispatch, body) => {
    dispatch({ type: POST_DESIGN, payload: body });
    dispatch(getAll());
  }
);

export const addDesign = (data) => (dispatch) => dispatch(postDesign(data)).unwrap();

// SCRUM-1814: the two answers to the 409 addDesign gets when only a deleted
// design holds the name or source. Both re-throw so the form can keep its offer
// on screen when the request fails, and refresh the list on success.

// Restore: bring the deleted design back under its reserved values.
export const restoreDesign = (id) => async (dispatch) => {
  try {
    const res: AxiosResponse<DesignRecoverResponse> = await api.post(`/api/design/${id}/restore`);
    dispatch(getAll());
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};

// Start fresh: release the deleted design's name and source for a new design.
export const releaseDesign = (id) => async (dispatch) => {
  try {
    const res: AxiosResponse<DesignRecoverResponse> = await api.post(`/api/design/${id}/release`);
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};

//GetAll designs
export const getAll = () => (dispatch) => {
  api
    .get("/api/design")
    .then((res) =>
      dispatch({
        type: GET_DESIGNS,
        payload: res.data,
      })
    )
    .catch((err) =>
      // SCRUM-856: surface the real error via the global notification hub instead
      // of dispatching GET_DESIGNS with null — the reducer calls `.reverse()` on the
      // payload, so null crashes it — and silently swallowing the failure. The
      // previous designs list is left in place.
      dispatch({
        type: GET_ERRORS,
        payload: err?.response?.data ?? { message: err?.message },
      })
    );
};

//Delete design
export const deleteDesign = (id) => async (dispatch) => {
  try {
    await api.delete<unknown>(`/api/design/delete/${id}`);
    dispatch({
      type: DELETE_DESIGN,
      payload: id,
    });
    return id;
  } catch (err) {
    // The old catch dispatched DELETE_DESIGN with the error body (a success action with
    // an error payload); record it as a proper error and re-throw instead (SCRUM-815).
    // SCRUM-2356: a refusal with referrers is the screen's pop-up, not a hub
    // error, so it is only re-thrown — the message then shows once.
    if (!isDeleteRefusal(asCaught(err)?.response?.data)) {
      dispatch({
        type: GET_ERRORS,
        payload: errorPayload(err),
      });
    }
    throw err;
  }
};
//update design
export const updateDesign = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<DesignResponse> = await api.put(
      "/api/design/update",
      data
    );
    dispatch({
      type: UPDATE_DESIGN,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: errorPayload(err),
    });
    throw err;
  }
};
